import { inject, Injectable } from '@angular/core';
import { HttpConfigInterface } from '../pagination/models/interfaces/http-config.interface';
import { Sorts } from '../pagination/models/typies/sorts.type';
import { RequestQueryBuilder, SCondition } from '@nestjsx/crud-request';
import { Joins } from '../pagination/models/typies/joins.type';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import * as xlsx from 'xlsx';
import { ProgressService } from '../../components/export/service/progress.service';
import { StringHelper } from '../../../helpers/string.helper';
import { Api } from '../api/api';
import { getCrudErrors } from '../../utils/functions';
import { environment } from '../../../../environments/environment';

@Injectable()
export class ExportService<T> {

  public isExporting: boolean = false;

  protected progressService: ProgressService = inject(ProgressService);

  protected api: Api = new Api();

  protected route!: string;

  protected sort!: Sorts;

  protected page: number = 0;

  protected pageSize: number = 0;

  protected totalPages: number = 1;

  protected exportInEachPage: boolean = false;

  protected data: T[] = [];

  protected total: number = 0;

  protected title: string = '';

  protected key: string = '';

  public async configExport(config: HttpConfigInterface): Promise<void> {
    this.title = config.title ?? 'Entidades';
    this.pageSize = config.pageSize ?? 10;
    this.route = config.route;
    this.sort = config.sort ?? { field: 'createdAt', order: 'DESC' };
    this.exportInEachPage = config.exportInEachPage ?? false;

    this.join = config.join ?? this.join;
    this.search = config.search ?? this.search;

    this.key = 'export-' + new StringHelper().randomCode(5);
  }

  public async export(): Promise<void> {
    if (this.isExporting)
      return;

    this.isExporting = true;

    let key = this.title + '-' + new StringHelper().randomCode(this.pageSize);
    let canExport = true;

    this.totalPages = 1;
    this.page = 0;

    this.data = [];

    this.progressService.new({
      key,
      total: this.totalPages,
      current: this.page,
      cancel: () => {
        canExport = false;
        this.api.abort(this.key);
        this.isExporting = false;
      },
      loading: true,
      name: 'Exportando: ' + this.title,
      canceled: false,
    });

    while (this.totalPages > this.page && canExport) {
      this.page = this.page + 1;

      const response = await this.loadData(this.route, this.page, 10);

      if (response.data.length === 0)
        break;

      this.total = response.total;
      this.totalPages = response.pageCount;

      if (this.exportInEachPage)
        this.exportToExcel(response.data);
      else
        this.data.push(...response.data);

      this.progressService.update(key, {
        total: this.totalPages,
        current: this.page,
        loading: false,
      });
    }

    if (!this.exportInEachPage && canExport)
      this.exportToExcel(this.data);

    this.isExporting = false;
    this.progressService.remove(key);
  }

  private exportToExcel(data: T[]): void {
    const formattedDataToExcel = this.formatExcel(data);

    const sheet = xlsx.utils.json_to_sheet(formattedDataToExcel);
    const workbook = xlsx.utils.book_new();
    const exportName = 'exportação'

    xlsx.utils.book_append_sheet(workbook, sheet, exportName);

    xlsx.writeFile(workbook, `${ exportName } v${ Date.now() }.xlsx`);
  }

  private async loadData(url: string, page: number, limit: number): Promise<PaginationDto<T>> {
    if (environment.useFakeBackend) {
      const mocked = await this.loadDataMocked(url, page, limit);

      if (mocked) {
        return mocked;
      }
    }

    let query = new RequestQueryBuilder()
      .setPage(page)
      .setLimit(limit)
      .setOffset(0)
      .sortBy(this.sort)
      .search({});

    query = await this.applySearch(query);
    query = await this.applyJoin(query);

    const { error, success } = await this.api.getAsync<PaginationDto<T>>(url, {
      params: query.queryObject,
      abortKey: this.key,
    });

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  private async applySearch(query: RequestQueryBuilder): Promise<RequestQueryBuilder> {
    const searchQuery = this.search();

    if (searchQuery)
      query = query.search({ ...searchQuery, });

    return query;
  }

  private async applyJoin(query: RequestQueryBuilder): Promise<RequestQueryBuilder> {
    const customJoinParams = this.join();

    if (customJoinParams)
      return query.setJoin(customJoinParams);

    return query;
  }

  protected async loadDataMocked(
    _url: string,
    _page: number,
    _limit: number,
  ): Promise<PaginationDto<T> | null> {
    return null;
  }

  protected search(): SCondition | undefined {
    return undefined;
  }

  protected join(): Joins | undefined {
    return undefined;
  }

  protected formatExcel(success: T[]): any {
    return success;
  }

}
